import { Alert, Button, Chip, IconButton, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import React, { Dispatch, FC, SetStateAction, useContext } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import AddNewTagModal from '../AddNewTagModal/AddNewTagModal';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import { toast } from 'react-toastify';
import { useGetEmployerTags } from '@features/applications/hooks/useGetEmployerTags';
import DeleteEmployerTagModal from '../DeleteEmployerTagModal/DeleteEmployerTagModal';
import { useEmployerAssignTags } from '@features/applications/hooks/useEmployerAssignTags';
import { lightGrey } from '@styles/colors';
import { authContext } from '@staffedupusa/shared-components';
import { Application } from '@features/applications/types';
import { Close } from '@mui/icons-material';
import { useGetTemplateTagsList } from '@features/enterprise/hooks/useGetTemplateTagsList';
import DeleteTagModal from '../DeleteTagModal/DeleteTagModal';

type EmployerAssignTagsModalProps = {
  manageTags?: boolean;
  templateId?: number;
  selectedApplicationsIds?: number[] | undefined;
  handleClose: () => void;
  handlePageNo?: () => void;
  setApplicationData?: Dispatch<SetStateAction<Application[] | null>>;
  setApplication?: Dispatch<SetStateAction<Application | null>>;
};

type Tag = {
  id: number;
  tag: string;
} | null;

const EmployerAssignTagsModal: FC<EmployerAssignTagsModalProps> = ({
  manageTags,
  templateId,
  selectedApplicationsIds,
  handleClose,
  handlePageNo,
  setApplicationData,
  setApplication,
}) => {
  const { authData } = useContext(authContext);
  const [selectedTags, setSelectedTags] = React.useState<number[]>([]);
  const [showNewTag, setShowNewTag] = React.useState<boolean>(false);
  const [editableTag, setEditableTag] = React.useState<Tag>(null);
  const [deleteTag, setDeleteTag] = React.useState<Tag>(null);
  const [errorText, setErrorText] = React.useState<string>('');

  const { tagsData, isLoadingTags } = useGetEmployerTags({
    setErrorText,
    enabled: authData?.user?.company ? true : false,
  });
  const { tagsList, isLoadingTagsList } = useGetTemplateTagsList({
    enabled: templateId ? true : false,
    setErrorText,
    templateId: templateId,
  });

  const { assignTags, isAssignTagsLoading } = useEmployerAssignTags({
    setErrorText,
  });

  const handleAddNewTag = () => {
    setShowNewTag(true);
  };

  const handleEditTag = (tag: { tag: string; id: number }) => {
    setEditableTag(tag);
  };

  const handleDeleteTag = (tag: { tag: string; id: number }) => {
    setDeleteTag(tag);
  };

  const handleSelectTag = (tag: number) => {
    setSelectedTags((prevSelectedTags) =>
      prevSelectedTags.includes(tag)
        ? prevSelectedTags.filter((t) => t !== tag)
        : [...prevSelectedTags, tag],
    );
  };

  const handleAssignTags = () => {
    const options = {
      onSuccess: (data: { message: string; applications: Application[] }) => {
        toast.success(data.message);
        handlePageNo && handlePageNo();
        const updatedSelectedApplication = data?.applications.find(
          (application: { id: any }) => application.id === application?.id,
        );
        if (updatedSelectedApplication && setApplication) {
          setApplication(updatedSelectedApplication);
        }

        setApplicationData &&
          updatedSelectedApplication &&
          setApplicationData((prev) => {
            if (!prev) return null;
            return prev?.map((application) => {
              if (application.id === updatedSelectedApplication.id) {
                return updatedSelectedApplication;
              }
              return application;
            });
          });
        handleClose();
      },
    };

    const tags = selectedTags;
    const application_id = selectedApplicationsIds ?? [];

    assignTags({ tags, application_id }, options);
  };

  return (
    <>
      {!manageTags && (
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={handleAddNewTag}
        >
          Add New
        </Button>
      )}
      {!manageTags ? (
        <Typography textAlign="center">Select From Following Tags</Typography>
      ) : null}
      {errorText && <Alert severity="error">{errorText}</Alert>}
      <Stack sx={{ maxHeight: '250px', overflow: 'auto' }}>
        {isLoadingTags || isLoadingTagsList ? (
          <Typography>Loading Tags...</Typography>
        ) : tagsData?.objects?.length || tagsList?.objects?.length ? (
          (tagsData?.objects || tagsList?.objects).map(
            (item: { id: number; tag: string; predefined?: any }) => (
              <Stack
                key={item.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ m: '3px !important' }}
              >
                <Chip
                  disabled={manageTags}
                  label={item.tag}
                  icon={
                    selectedTags.includes(item.id) ? (
                      <CheckIcon color="inherit" />
                    ) : undefined
                  }
                  sx={{
                    borderRadius: 5,
                    color: 'black',
                    backgroundColor: lightGrey,
                    px: 2,
                    py: 1,
                    '&.Mui-disabled': {
                      color: 'black',
                      backgroundColor: `${lightGrey} !important`,
                      opacity: 1,
                    },
                  }}
                  onClick={() => handleSelectTag(item.id)}
                />
                <Stack direction="row">
                  <IconButton
                    disabled={item.predefined === 1}
                    onClick={() => handleEditTag(item)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    disabled={item.predefined === 1}
                    onClick={() => handleDeleteTag(item)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            ),
          )
        ) : (
          <Typography>No Tags Available</Typography>
        )}
      </Stack>
      {manageTags ? (
        <>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            onClick={handleAddNewTag}
          >
            Add New
          </Button>
          <Button
            startIcon={<Close />}
            variant="outlined"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </>
      ) : (
        <Button
          startIcon={<DoneIcon />}
          sx={{ marginBottom: '10px !important' }}
          variant="contained"
          color="success"
          disabled={selectedTags.length === 0 || isAssignTagsLoading}
          onClick={handleAssignTags}
        >
          {isAssignTagsLoading ? 'Loading...' : 'Save'}
        </Button>
      )}
      {(showNewTag || editableTag) && (
        <AddNewTagModal
          handleClose={() => {
            setShowNewTag(false);
            setEditableTag(null);
          }}
          editable={editableTag ?? null}
          templateId={templateId}
        />
      )}
      {deleteTag && !templateId && (
        <DeleteEmployerTagModal
          handleClose={() => setDeleteTag(null)}
          deleteTag={deleteTag}
        />
      )}
      {deleteTag && templateId && (
        <DeleteTagModal
          handleClose={() => setDeleteTag(null)}
          deleteTag={deleteTag}
          templateId={templateId}
        />
      )}
    </>
  );
};

export default EmployerAssignTagsModal;
